import type { ApiClient } from '../client/api-client';
import type {
  LoginRequestDTO,
  RegisterRequestDTO,
  LoginConfirmRequestDTO,
  Enable2FAConfirmRequestDTO,
  ForgotPasswordRequestDTO,
  ResetPasswordCheckRequestDTO,
  ResetPasswordCheckResponseDTO,
  ResetPasswordRequestDTO,
  ChangePasswordRequestDTO,
  AuthResponseDTO,
  Enable2FAResponseDTO,
  UserDTO,
} from '../contracts/auth';
import type { TwoFaSetupDto } from '../contracts/security';

export function createAuthApi(client: ApiClient) {
  return {
    /**
     * Login with email and password
     * POST /app/v1/auth/login
     */
    async login(payload: LoginRequestDTO): Promise<AuthResponseDTO> {
      return client.post<AuthResponseDTO>('/app/v1/auth/login', payload);
    },

    /**
     * Confirm login with 2FA code
     * POST /app/v1/auth/login-confirm
     */
    async loginConfirm(payload: LoginConfirmRequestDTO, accessToken?: string): Promise<AuthResponseDTO> {
      const body = payload.secondFactor
        ? { secondFactor: payload.secondFactor }
        : { secondFactor: { method: 'totp' as const, code: payload.code ?? '' } };
      if (accessToken) {
        return client.postWithAccessToken<AuthResponseDTO>('/app/v1/auth/login-confirm', body, accessToken);
      }
      return client.post<AuthResponseDTO>('/app/v1/auth/login-confirm', body);
    },

    async sendLoginEmailOtp(accessToken: string): Promise<{ sentTo?: string | null; resendAfterSec?: number }> {
      return client.postWithAccessToken('/app/v1/auth/login/otp/email', {}, accessToken);
    },

    async setupLoginTwoFa(
      payload: { method: 'totp' | 'email' },
      accessToken: string,
    ): Promise<TwoFaSetupDto> {
      return client.postWithAccessToken('/app/v1/auth/2fa/setup', payload, accessToken);
    },

    async confirmLoginTwoFa(
      payload: { method: 'totp' | 'email'; code: string },
      accessToken: string,
    ): Promise<AuthResponseDTO> {
      return client.postWithAccessToken('/app/v1/auth/2fa/setup-confirm', payload, accessToken);
    },

    async confirmDevice(payload: { code: string }, accessToken: string): Promise<AuthResponseDTO> {
      return client.postWithAccessToken('/app/v1/auth/login-confirm-device', payload, accessToken);
    },

    async resendDeviceCode(accessToken: string): Promise<{ sentTo?: string | null; resendAfterSec?: number }> {
      return client.postWithAccessToken('/app/v1/auth/login-confirm-device/resend', {}, accessToken);
    },

    /**
     * Register new user
     * POST /app/v1/registration
     */
    async register(payload: RegisterRequestDTO): Promise<AuthResponseDTO> {
      return client.post<AuthResponseDTO>('/app/v1/registration', payload);
    },

    /**
     * Send email verification code
     * POST /app/v1/auth/send-email
     */
    async sendEmail(): Promise<void> {
      await client.post<void>('/app/v1/auth/send-email', {});
    },

    /**
     * Confirm email from link token
     * POST /app/v1/auth/check-email/{token}
     */
    async checkEmail(token: string): Promise<AuthResponseDTO> {
      return client.post<AuthResponseDTO>(`/app/v1/auth/check-email/${token}`, {});
    },

    /**
     * Get current user data
     * GET /app/v1/users/me
     */
    async getUser(): Promise<UserDTO> {
      return client.get<UserDTO>('/app/v1/users/me');
    },

    /**
     * Enable 2FA - get QR code
     * POST /app/v1/users/enable2fa
     */
    async enable2fa(): Promise<Enable2FAResponseDTO> {
      return client.post<Enable2FAResponseDTO>('/app/v1/users/enable2fa', {});
    },

    /**
     * Confirm 2FA enable with code
     * POST /app/v1/users/enable2fa-confirm
     */
    async enable2faConfirm(payload: Enable2FAConfirmRequestDTO): Promise<AuthResponseDTO> {
      return client.post<AuthResponseDTO>('/app/v1/users/enable2fa-confirm', payload);
    },

    /**
     * Logout current session (ADMIN and MERCHANT). Uses the HttpOnly refresh-token cookie, no access token needed.
     * POST /app/v1/auth/logout
     */
    async logout(): Promise<void> {
      return client.post<void>('/app/v1/auth/logout', {});
    },

    /**
     * Refresh referral code
     * PATCH /app/v1/users/me/refresh-referral
     */
    async refreshReferralCode(): Promise<UserDTO> {
      return client.patch<UserDTO>('/app/v1/users/me/refresh-referral', {});
    },

    /**
     * Request a password reset link. Always 204, even when the email is unknown.
     * POST /app/v1/auth/password/forgot
     */
    async forgotPassword(payload: ForgotPasswordRequestDTO): Promise<void> {
      await client.post<void>('/app/v1/auth/password/forgot', payload);
    },

    /**
     * Check a reset link token.
     * POST /app/v1/auth/password/reset/check
     */
    async checkResetPassword(payload: ResetPasswordCheckRequestDTO): Promise<ResetPasswordCheckResponseDTO> {
      return client.post<ResetPasswordCheckResponseDTO>('/app/v1/auth/password/reset/check', payload);
    },

    /**
     * Set a new password from a reset link.
     * POST /app/v1/auth/password/reset
     */
    async resetPassword(payload: ResetPasswordRequestDTO): Promise<void> {
      await client.post<void>('/app/v1/auth/password/reset', payload);
    },

    /**
     * Change password for the current session. Response includes a new access token.
     * POST /app/v1/users/me/change-password
     */
    async changePassword(payload: ChangePasswordRequestDTO): Promise<AuthResponseDTO> {
      return client.post<AuthResponseDTO>('/app/v1/users/me/change-password', payload);
    },
  };
}

export type AuthApi = ReturnType<typeof createAuthApi>;
